<template>
  <div class="d-flex flex-column flex-md-row justify-md-space-between">
    <div>
      <h1 class="">{{ location?.name }}</h1>
      <div
        v-if="location?.settings?.rating"
        class="d-flex align-center rating"
        aria-label="Bewertung"
      >
        <span class="ml-2 text-h5 text-accent rating-value">{{ location.settings?.rating }}</span>
        <v-rating
          :model-value="location.settings.rating"
          color="accent"
          half-increments
          readonly
          :aria-label="'Bewertung: ' + location.settings?.rating + ' von 5'"
        ></v-rating> <span v-if="location.settings?.reviews">({{ location.settings?.reviews }})</span>
      </div>
    </div>
    <div>
      <Button
        :disabled-client="isLoading"
        @click="bookAppointment"
        aria-label="Termin buchen"
      >Termin buchen</Button>
    </div>
  </div>

  <div class="d-flex py-5 flex-column">
    <!-- header hero with image -->
    <v-img
      :src="headerImage ?? `/api/map?address=${encodeURIComponent(location?.street + ' ' + location?.house_nr + ', ' + location?.zip + ' ' + location?.city)}`"
      :lazy-src="cardHeaderImage"
      :aspect-ratio="mobile ? 1 : 2.75"
      class="rounded-lg"
      :cover="true"
      :alt="`${location?.name} - Hauptbild`"
      :max-height="450"
      aria-label="Hauptbild von {{ location?.name }}"
    ></v-img>

    <!-- company info -->
    <div
      v-if="location"
      class="py-5 text-black"
      aria-label="Unternehmensinformationen"
    >
      <span class="mb-2 d-block"><v-icon
          icon="ri-road-map-line"
          aria-label="Adresse"
        /> {{ formatAddress(location) }}</span>
      <span class="mb-2 d-block"><v-icon
          icon="ri-door-open-line"
          aria-label="Heutige Öffnungszeiten"
        /> {{ showTodaysOpeningHours(location) }}</span>
      <span class="mb-2 d-block"><v-icon
          icon="ri-timer-flash-line"
          aria-label="Schließt in Stunden"
        /> {{ calculateClosesInHours(location) }}</span>
      <span
        class="d-block mb-2"
        v-if="location?.settings?.pre_payment"
      ><v-icon
          icon="ri-information-line"
          aria-label="Vorkasse"
        />
        Vorkasse erforderlich
      </span>
      <span class="d-block">
        <v-chip
          v-for="language in location?.settings?.supportedLanguages"
          :key="language"
          class="me-2 mr-2 mb-2"
          variant="outlined"
          :aria-label="`Unterstützte Sprache: ${mapLanguage(language)}`"
        >
          <v-avatar start>
            <v-img
              :src="`https://flagcdn.com/w160/${language.toLowerCase()}.jpg`"
              :width="20"
              :height="20"
              cover
              :alt="language"
              :aria-label="`Flagge von ${mapLanguage(language)}`"
            />
          </v-avatar>
          {{ mapLanguage(language) }}
        </v-chip>
      </span>
    </div>
  </div>

  <div class="animated-bar mb-5"></div>

  <v-row class="py-5">
    <v-col
      cols="12"
      md="4"
    >
      <h4
        class="text-h5"
        aria-label="Öffnungszeiten"
      >Öffnungszeiten</h4>
    </v-col>

    <v-col
      cols="12"
      md="8"
      class="text-left text-md-right"
    >
      <OpeningHours
        :opening-hours="location?.openingHours"
        aria-label="Öffnungszeiten Details"
      />
    </v-col>
  </v-row>

  <div class="animated-bar mb-5"></div>

  <!-- services -->

  <v-row
    class="py-5"
    v-if="location?.services && location?.services?.length > 0"
    aria-label="Dienstleistungen"
  >
    <v-col
      cols="12"
      id="services"
    >
      <div class="d-flex justify-space-between flex-column flex-md-row">
        <h4 class="text-h5">Dienstleistungen</h4>
        <Button
          :disabled="isLoading"
          @click="bookAppointment"
          aria-label="Termin buchen"
          class="d-block d-md-inline-block w-100 w-md-auto"
        >Termin buchen</Button>
      </div>

    </v-col>
    <v-col cols="12">
      <v-alert
        color="primary"
        icon="ri-information-line"
        title="Information"
        variant="tonal"
        :timeout="5000"
        class="mb-5"
        v-model="showAlert"
        aria-label="Bitte wählen Sie eine Dienstleistung aus, um einen Termin zu buchen."
      >
        Bitte wählen Sie eine Dienstleistung aus, um einen Termin zu buchen.
      </v-alert>

      <!-- @TOOD make it as a select list/menu on mobile -->
      <v-chip-group
        v-model="selectedCategory"
        v-if="serviceCategories.length > 1"
        class="mb-4 d-flex flex-wrap justify-start"
        mandatory
        selected-class="bg-primary text-white"
        aria-label="Dienstleistungskategorien"
      >
        <v-chip
          :value="null"
          class="ma-1"
          filter
          variant="outlined"
          aria-label="Alle Kategorien"
        >
          <v-icon
            v-if="selectedCategory === null"
            icon="ri-close-line"
            start
            size="small"
          />
          Alle
        </v-chip>
        <v-chip
          v-for="category in serviceCategories"
          :key="category.id"
          :value="category.id"
          class="ma-1"
          filter
          variant="outlined"
          :aria-label="category.name"
        >
          <v-icon
            :icon="getCategoryIcon(category.id as ServiceCategory)"
            start
            size="small"
          />
          {{ category.name }}
        </v-chip>
      </v-chip-group>

      <v-card
        v-for="service in filteredServices"
        :key="service.id"
        class="mb-4 hover:bg-gray-100"
        flat
        :aria-label="`Dienstleistung: ${service.name}`"
      >
        <v-card-item>
          <v-row align="center">
            <v-col
              cols="12"
              sm="6"
            >
              <v-card-title class="text-h6">{{ service.name }}</v-card-title>
              <v-card-subtitle class="py-2">
                <v-icon
                  :icon="getCategoryIcon(service.category as ServiceCategory)"
                  size="small"
                  class="mr-1"
                  :aria-label="`Kategorie: ${ServiceCategory[service.category as keyof typeof ServiceCategory]}`"
                />
                {{ ServiceCategory[service.category as keyof typeof ServiceCategory] }}
                <v-icon
                  icon="ri-time-line"
                  size="small"
                  class="mr-1"
                  aria-label="Dauer"
                />
                {{ service.duration }} Minuten
              </v-card-subtitle>
              <v-card-text class="pa-0">
                {{ service.description }}
              </v-card-text>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              class="d-flex justify-end align-center"
            >
              <v-card-text class="text-h6 mr-4">{{ service.price }} €</v-card-text>
              <Button
                :color="service.selected ? 'primary' : 'default'"
                :variant="service.selected ? 'tonal' : 'outlined'"
                :disabled="service.selected"
                @click="addToCart(service)"
                :aria-label="service.selected ? 'Im Warenkorb' : 'Auswählen'"
              >
                <v-icon
                  :icon="service.selected ? 'ri-check-line' : 'ri-add-line'"
                  class="mr-2"
                />
                <span class="d-none d-sm-inline">
                  {{ service.selected ? 'Im Warenkorb' : 'Auswählen' }}
                </span>
              </Button>
            </v-col>
          </v-row>
        </v-card-item>
      </v-card>

      <v-row>
        <v-col
          cols="12"
          class="d-flex justify-end"
        >
          <Button
            :disabled="isLoading"
            @click="bookAppointment"
            aria-label="Termin buchen"
            class="d-block d-md-inline-block w-100 w-md-auto"
          >Termin buchen</Button>
        </v-col>
      </v-row>
    </v-col>
  </v-row>

  <!-- qr code -->

  <v-row
    v-if="location"
    class="mt-8"
    aria-label="QR-Code teilen"
  >
    <v-col
      cols="12"
      class="d-flex justify-center"
    >
      <v-row>
        <v-col
          cols="12"
          md="8"
          class="d-flex flex-column justify-center"
        >
          <h4 class="text-h5 mb-4">Teilen Sie diesen Standort</h4>
          <p class="mb-4">Scannen Sie den QR-Code, um diesen Standort schnell und einfach mit anderen zu teilen. Perfekt
            für Empfehlungen an Freunde und Familie!</p>
          <ShareComponent :location="location" />
        </v-col>
        <v-col
          cols="12"
          md="4"
          class="d-flex justify-end"
        >

          <v-card class="pa-4 rounded-lg w-100 w-md-auto">
            <v-card-title class="text-center mb-4">
              QR-Code scannen
            </v-card-title>
            <v-img
              :src="'/api/locations/qrcode?id=' + location?.id"
              aspect-ratio="1"
              class="rounded-lg mx-auto"
              :cover="true"
              max-width="250"
              :alt="`QR-Code für ${location.name}`"
              aria-label="QR-Code für {{ location.name }}"
            >
              <template v-slot:placeholder>
                <v-row
                  class="fill-height ma-0"
                  align="center"
                  justify="center"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    aria-label="Lädt"
                  ></v-progress-circular>
                </v-row>
              </template>
            </v-img>
            <v-card-text class="text-center mt-4">
              Scannen, um diesen Standort zu teilen
            </v-card-text>
          </v-card>

        </v-col>
      </v-row>
    </v-col>
  </v-row>

  <!-- description + map -->

  <div class="animated-bar my-5"></div>

  <v-row>
    <v-col cols="12">
      <div
        class="text-h5"
        aria-label="Beschreibung"
      >Beschreibung</div>
      <div
        class="text-body-1 markdown-body"
        v-html="mdDescription"
      ></div>
    </v-col>
    <v-col cols="12">
      <v-row>
        <v-col
          cols="12"
          md="6"
        >
          <h4
            class="text-h5 mb-2"
            aria-label="Karte"
          >{{ location!.name }}</h4>
          <a
            :href="'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(location?.street + ' ' + location?.house_nr + ', ' + location?.zip + ' ' + location?.city)"
            rel="noopener noreferrer nofollow"
            target="_blank"
          > <v-icon icon="mdi-map"></v-icon> {{ location?.street + ' '
            + location?.house_nr + ', ' + location?.zip + ' '
            + location?.city }}
          </a>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <div
            class="text-h5 mb-2"
            aria-label="Karte"
          >Karte</div>
          <div class="text-body-1">
            <v-img
              :alt="`Karte von ${location?.name}`"
              :src="`/api/map?address=${encodeURIComponent(location?.street + ' ' + location?.house_nr + ', ' + location?.zip + ' ' + location?.city)}`"
              aria-label="Karte von {{ location?.name }}"
            />
          </div>
        </v-col>
      </v-row>
    </v-col>

    <v-col cols="12">
      <h4
        class="text-h5"
        aria-label="Bewertungen"
      >Bewertungen</h4>
      <div
        class="text-body-1"
        v-if="reviews.length > 0"
        aria-label="Bewertungsliste"
      >
        <v-list>
          <v-list-item
            v-for="review in reviews"
            :key="review.id"
            class="mb-4"
            :aria-label="`Bewertung von ${review.user_name}`"
          >
            <template #default>
              <div class="d-flex align-center mb-2">
                <v-avatar
                  color="primary"
                  size="36"
                  class="mr-3"
                  :aria-label="`Avatar von ${review.user_name}`"
                >
                  {{ review.user_name?.charAt(0).toUpperCase() }}
                </v-avatar>
                <div>
                  <v-list-item-title class="font-weight-bold">{{ review.user_name }}</v-list-item-title>
                  <v-list-item-subtitle class="d-flex align-center">
                    <v-rating
                      :model-value="review.rating"
                      color="amber"
                      :length="5"
                      half-increments
                      readonly
                      size="small"
                      class="mr-2"
                      :aria-label="`Bewertung: ${review.rating.toFixed(1)} von 5`"
                    ></v-rating>
                    <span class="text-caption">{{ review.rating.toFixed(1) }}</span>
                  </v-list-item-subtitle>
                </div>
              </div>
              <div class="text-body-2">{{ review.comment }}</div>
            </template>
          </v-list-item>
        </v-list>
      </div>
      <div
        class="text-body-1"
        v-else
        aria-label="Keine Bewertungen"
      >
        <div class="text-body-1 mt-4">
          <v-alert
            type="info"
            density="comfortable"
            aria-label="Keine Bewertungen vorhanden"
          >
            Entdecken Sie unseren Service und teilen Sie Ihre Erfahrung! Ihre ehrliche Bewertung nach einem Besuch
            bei uns hilft nicht nur uns, sondern auch anderen Kunden. Buchen Sie einen Termin und erleben Sie
            erstklassigen Service. Wir freuen uns auf Ihr wertvolles Feedback!
          </v-alert>
        </div>
      </div>
    </v-col>
  </v-row>

  <!-- if no booking is possible -->
  <v-dialog
    v-model="noBookingDialog"
    max-width="490"
    min-width="300"
  >
    <v-card class="pa-5">
      <v-card-title class="pa-0 mb-2">
        Es geht leider nur per Telefon
      </v-card-title>
      <v-card-text class="pa-0">
        Leider hat sich die Einrichtung:
        <b class="d-block my-2">{{ location?.name }}</b>
        noch nicht für die Online-Buchung entschieden.
        Sprechen Sie den Betreiber doch vor Ort direkt mal darauf an und verdienen
        Sie <b>25 &euro;</b> als Provision bei erfolgreicher Vermittlung.
        <br>
        <br>
        <span v-if="location?.phone">
          Die Telefonnummer lautet: <a :href="'tel:' + location?.phone">{{ location?.phone }}</a><br>
        </span>
        <span v-if="location.url">
          Besuchne Sie die Webseite: <a
            rel="nofollow noindex"
            :href="location?.url"
            target="_blank"
          >{{ location?.url
            }}</a>
        </span>
      </v-card-text>
      <v-card-actions>
        <slot name="actions">
          <v-spacer></v-spacer>
          <v-btn
            color="default"
            @click="noBookingDialog = false"
          >Schliessen</v-btn>
        </slot>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- warenkorb -->

  <div
    ref="cartContainer"
    class="animated-cart pa-5 pa-md-5 mobile-hidden"
    @click.stop="showCartOnMobile"
    v-if="cart.length > 0"
    aria-label="Warenkorb"
  >
    <v-container
      class="px-0 px-md-5"
    >
      <v-row>
        <v-col
          cols="12"
          class="py-0 d-flex justify-space-between align-center mb-4"
        >
          <div class="text-h5"><v-icon
              icon="ri-shopping-cart-line"
              class="mr-2 text-h6-"
              aria-label="Warenkorb Symbol"
            />
            Warenkorb ({{ cart.length }})
          </div>
          <v-btn
            icon
            small
            variant="text"
            color="grey"
            density="compact"
            aria-label="Warenkorb schließen"
            class="d-block d-md-none"
            @click.stop="hideCartOnMobile"
            role="button"
          >
            <v-icon>ri-close-line</v-icon>
          </v-btn>
        </v-col>

        <v-col
          cols="12"
          md="8"
          class="overflow-y-auto py-0"
        >
          <v-list class="py-0">
            <v-list-item
              v-for="service in cart"
              :key="service.id"
              class="mb-2 d-flex justify-space-between pa-0 align-center"
              :aria-label="`Warenkorb Artikel: ${service.name}`"
            >
              <v-list-item-title class="font-weight-medium">
                {{ service.name }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ service.price }} €
              </v-list-item-subtitle>

              <template v-slot:append>
                <v-btn
                  icon
                  variant="text"
                  color="black"
                  density="compact"
                  size="small"
                  @click="removeFromCart(service)"
                  class="ml-2"
                  aria-label="Aus Warenkorb entfernen"
                >
                  <v-icon>ri-delete-bin-line</v-icon>
                </v-btn>
              </template>
            </v-list-item>
          </v-list>
        </v-col>
        <v-col
          cols="12"
          md="4"
          class="d-flex flex-column"
        >
          <div>
            <div class="text-h6 mb-2">Gesamtpreis:</div>
            <div class="text-h4 font-weight-bold mb-4">{{ calculateCartTotal() }} €</div>
          </div>
          <Button
            color="primary"
            @click="bookAppointment"
            class="mt-4"
            block
            aria-label="Termin buchen"
          >
            Termin buchen
          </Button>
          <Button
            color="default"
            variant="outlined"
            class="mt-2 d-block d-md-none"
            block
            @click.stop="hideCartOnMobile"
            aria-label="Warenkorb schließen"
          >
            Warenkorb schließen
          </Button>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script lang="ts" setup>
import { Language, ServiceCategory, ServiceCategoryIcon, type CompanyLocation, type Review, type Service } from '@/types';
import headerGenerator from '@/utils/headerGenerator';
import { useLocationStore } from '~/store/locationStore';
import OpeningHours from '@/components/OpeningHours.vue';
import Button from '~/components/Button.vue';
import { formatAddress } from '@/utils/formatters';
import { useDisplay } from 'vuetify';
import ShareComponent from '~/components/public/ShareComponent.vue';
import { marked } from 'marked';
import { LanguageName } from '~/types/constants';

const cardHeaderImage = '/images/misc/friseur-salon-no-image.jpg';

definePageMeta({
  layout: 'default-public-single',
  middleware: 'check-if-exists'
});

const route = useRoute();
const locationStore = useLocationStore();
const locationId = route.params.id as string;
const location = ref<typeof CompanyLocation | null>(null);
const isLoading = ref(true);
const cart = ref<typeof Service[]>([]);
const cartContainer = ref<HTMLDivElement | null>(null);
const { mobile } = useDisplay();
const showAlert = ref(false);
const reviews = ref<typeof Review[]>([]);
const selectedCategory = ref<ServiceCategory | null>(null);
const headerImage = ref<string | null>(null);
const mdDescription = ref<string | null>(null);
const serviceCategories = ref(Object.entries(ServiceCategory).map(([key, value]) => ({
  id: key,
  name: value
})));
const filteredServices = ref<typeof Service[]>([]);
const noBookingDialog = ref(false);

watch(selectedCategory, () => {
  if (!selectedCategory.value) {
    filteredServices.value = location.value?.services || [];
  } else {
    filteredServices.value = location.value?.services.filter(service => service.category === selectedCategory.value) || [];
  }
});

const mapLanguage = (language: Language): string => {
  return LanguageName[language];
}

/**
 * Loads the location from the database
 * @returns The location
 */
const loadLocation = async (): Promise<void> => {
  location.value = await locationStore.loadLocation(locationId);

  filteredServices.value = location.value?.services || [];
  headerImage.value = location.value?.image;
  mdDescription.value = marked(location.value?.description || '');

  // Filter service categories based on location services
  const availableCategories = location.value?.services.reduce((acc, service) => {
    if (service.category && !acc.includes(service.category)) {
      acc.push(service.category);
    }
    return acc;
  }, [] as ServiceCategory[]) || [];

  // Update serviceCategories to only include available categories
  serviceCategories.value = Object.entries(ServiceCategory)
    .filter(([key]) => availableCategories.includes(key as ServiceCategory))
    .map(([key, value]) => ({
      id: key,
      name: value
    }));
}

const getCategoryIcon = (category: ServiceCategory): string => {
  return ServiceCategoryIcon[category as keyof typeof ServiceCategoryIcon] || ServiceCategoryIcon.Other;
}

/**
 * Loads the services of a location from the database
 * @param locationId The id of the location to load the services from
 * @returns The services of the location
 */
const parseCart = async () => {
  const localCart = localStorage.getItem('cart_' + locationId);
  if (localCart) {
    try {
      cart.value = JSON.parse(localCart) as typeof Service[];

      location.value?.services.forEach(service => {
        if (cart.value.some(cartService => cartService.id === service.id)) {
          service.selected = true;
        }
      });
    } catch (error) {
      cart.value = [];
    }
  }
}

/**
 * Shows the todays opening hours of the location
 * @returns The todays opening hours
 */
const showTodaysOpeningHours = locationStore.showTodaysOpeningHours;
/**
 * Calculates the time until the location closes
 * @returns The time until the location closes
 */
const calculateClosesInHours = locationStore.calculateClosesInHours;

/**
 * Adds a service to the cart
 * @param service The service to add to the cart
 */
const addToCart = async (service: typeof Service) => {
  cart.value.push(service);

  location.value?.services.forEach(service => {
    if (cart.value.some(cartService => cartService.id === service.id)) {
      service.selected = true;
    }
  });

  localStorage.setItem('cart_' + locationId, JSON.stringify(cart.value));
}

/**
 * Books an appointment
 */
const bookAppointment = () => {
  if (!location.value?.company_id) {
    noBookingDialog.value = true;
    return;
  }

  if (location.value?.services?.length > 0 && cart.value.length === 0) {
    showAlert.value = true;

    const serviceElement = document.getElementById('services');
    if (serviceElement) {
      serviceElement.scrollIntoView({ behavior: 'smooth' });
    }

    return;
  }

  navigateTo('/listings/' + location.value!.id + '/booking');
}

/**
 * Calculates the total price of the cart
 * @returns The total price of the cart
 */
const calculateCartTotal = (): number => {
  return cart.value.reduce((total, service) => total + (service.price || 0), 0);
}

/**
 * Removes a service from the cart
 * @param service The service to remove from the cart
 */
const removeFromCart = (service: typeof Service) => {
  cart.value = cart.value.filter(s => s.id !== service.id);

  location.value?.services.forEach(service => {
    if (cart.value.some(cartService => cartService.id === service.id)) {
      service.selected = true;
    } else {
      service.selected = false;
    }
  });

  localStorage.setItem('cart_' + locationId, JSON.stringify(cart.value));
}

/**
 * Shows the cart on mobile
 */
const showCartOnMobile = () => {
  if (mobile.value && cartContainer.value) {
    cartContainer.value.classList.remove('mobile-hidden');
    cartContainer.value.classList.add('mobile-visible');
  }
}

const hideCartOnMobile = () => {
  if (mobile.value && cartContainer.value) {
    cartContainer.value.classList.remove('mobile-visible');
    cartContainer.value.classList.add('mobile-hidden');
  }
}

const loadReviews = async () => {
  reviews.value = await locationStore.loadLocationReviews(location.value!.id);
}

await loadLocation();

const header = headerGenerator(null, location.value!);

useHead({
  ...header,
  meta: [
    ...header.meta,
    {
      hid: 'image',
      property: 'image',
      content: location.value?.image || ''
    },
    {
      hid: 'ratingValue',
      property: 'ratingValue',
      content: location.value?.settings?.rating?.toString() || '0'
    },
    {
      hid: 'reviewCount',
      property: 'reviewCount',
      content: reviews.value?.length?.toString() || '0'
    }
  ]
});

onMounted(async () => {
  parseCart();
  await loadReviews();

  isLoading.value = false;
});
</script>

<style lang="scss" scoped>
@import '@/node_modules/vuetify/lib/styles/settings/_variables';

$cart-height: 70px;

.hover\:bg-gray-100:hover {
  background-color: #f0f0f0;
}

.animated-cart {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: #fff;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  animation: slideIn 0.5s ease-in-out;
  height: auto;
  overflow: hidden;
  z-index: 10;

  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    &.mobile-hidden {
      height: $cart-height !important;
      overflow: hidden;
    }

    &.mobile-visible {
      height: calc(100vh - $cart-height) !important;
      overflow: hidden;
    }
  }

  &:not(:hover) {
    height: $cart-height;
    overflow: hidden;
    transition: height 0.3s ease-in-out, max-height 0.3s ease-in-out;
  }

  &:hover {
    height: calc(100vh - $cart-height);
    transition: height 0.3s ease-in-out, max-height 0.3s ease-in-out;
  }

  &::before {
    content: "▲";
    position: absolute;
    top: 5px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 12px;
    color: #888;
    transition: transform 0.3s ease-in-out;
  }

  &:hover::before {
    // opacity: 0;
    transform: rotate(180deg);
  }
}

:deep(.markdown-body) {

  ul,
  ol {
    padding-left: 1.5em;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
}

.rating {
  .rating-value {
    font-weight: bold;
    position: relative;
    top: 3px
  }
}
</style>
